import { Directive, HostListener, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * PhoneFormatDirective
 * Automatically formats a phone input to US format: (555) 555-5555
 * Selector: input[appPhoneFormat]
 *
 * - Stores the formatted value (e.g. "(555) 555-5555") in the form control
 * - Strip non-digits before sending to API: value.replace(/\D/g, '')
 */
@Directive({
  selector: 'input[appPhoneFormat]',
  standalone: true,
})
export class PhoneFormatDirective {
  private ngControl = inject(NgControl, { optional: true });

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    const formatted = this.format(digits);

    // Update the visible input directly to avoid cursor-jump issues
    const cursorPos = this.getNewCursorPos(input.selectionStart ?? 0, formatted);
    input.value = formatted;
    input.setSelectionRange(cursorPos, cursorPos);

    // Store formatted value in the control — strip \D before sending to API
    this.ngControl?.control?.setValue(formatted, { emitEvent: true, emitModelToViewChange: false });
  }

  @HostListener('blur')
  onBlur(): void {
    const ctrl = this.ngControl?.control;
    if (!ctrl) return;
    const digits = (ctrl.value ?? '').replace(/\D/g, '').slice(0, 10);
    const formatted = this.format(digits);
    ctrl.setValue(formatted, { emitEvent: false, emitModelToViewChange: false });
  }

  /** Build (555) 555-5555 progressively as digits are typed */
  private format(digits: string): string {
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  /** Keep cursor after the last digit that was just typed */
  private getNewCursorPos(oldPos: number, formatted: string): number {
    // Count digits up to old cursor position to track intent, then find same count in formatted
    return Math.min(oldPos + 1, formatted.length);
  }
}
